import React, {useRef, useState} from 'react';
import {Controller, useForm, useWatch} from 'react-hook-form';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import {zodResolver} from '@hookform/resolvers/zod';
import {observer} from 'mobx-react-lite';

import {otp, space} from '../../design-system/tokens';
import {VerificationFormData, verificationSchema} from '../../screens/account/schemas/authSchemas';
import {useWideLayout} from '../../screens/account/signUp/SignUpPageLayout';
import {cleanPhoneNumber, formatMobileForDisplay} from '../../utils/phoneNumber';
import Otp from '../ui-controls/form/Otp';
import ContentView from '../ui-controls/layout/ContentView';

import PhoneVerificationView from './PhoneVerificationView';
import {RESEND_ERROR_MESSAGE, slowDown} from './slowDown';

/**
 * Timeout for sending the code
 */
const TIMEOUT = 30000;

/**
 * Mirrors `verificationCodeSchema.length(6)` in
 * screens/account/schemas/authSchemas. Zod exposes no way to read a length
 * check back off the schema, so the number is restated here; change both.
 */
const VERIFICATION_CODE_LENGTH = 6;

type Props = {
  /**
   * This is used to show the user the number the sms was sent to
   */
  phoneNumber?: string;

  /**
   * This is used to show the user the email the email was sent to
   */
  emailAddress?: string;

  /**
   * handler when verify button cta is pressed
   * @param values
   * @param helpers
   * @returns
   */
  onSubmit: (
    values: VerificationFormData,
    helpers: {
      setError: (field: 'verification', error: {type: string; message: string}) => void;
    },
  ) => Promise<void>;

  /**
   * Handler to resend the verification code.
   * Should return true if success false if failed.
   * @returns
   */
  onResend: () => Promise<boolean>;

  /**
   * Handle back button press
   * @returns
   */
  onBack: () => void;

  /**
   * Type of verification
   */
  type: 'email' | 'phone';

  /**
   * On web at the wide breakpoint, render the desktop centred card
   * (PhoneVerificationView `centredOnLayout`). Opt-in so the UPDATE flow and
   * the email screen keep the narrow column.
   */
  centredOnWide?: boolean;
};

// Desktop card: Figma 536:4581's fixed 49 x 52 boxes, as on the Lite verify screen.
const otpBoxSize = {width: otp.boxWidth, height: otp.boxHeight};

/**
 * This screen component is used in sign up flows where phone number verification is needed.
 */
const PhoneOrEmailVerification = observer(
  ({
    emailAddress,
    phoneNumber,
    onSubmit: _onSubmit,
    onResend: _onResend,
    onBack,
    type,
    centredOnWide = false,
  }: Props) => {
    const {isDesktopCard, onLayout} = useWideLayout();
    const isWide = centredOnWide && isDesktopCard;
    const [resending, setResending] = useState(false);
    const [codeResent, setCodeResent] = useState(false);
    const [resendError, setResendError] = useState<string | undefined>(undefined);

    const {
      control,
      handleSubmit,
      formState: {errors, isSubmitting},
      reset,
      setError,
    } = useForm<VerificationFormData>({
      resolver: zodResolver(verificationSchema),
      defaultValues: {
        verification: '',
      },
      // DIS-1824: onTouched, not onBlur. After the first blur every change
      // re-validates, so a length error from a part-typed code clears once the
      // code reaches 6 digits instead of staying until the next blur.
      mode: 'onTouched',
    });

    // formState alone cannot gate VERIFY on the digits typed so far. useWatch
    // re-renders on this one field only.
    const verificationValue = useWatch({control, name: 'verification'}) ?? '';
    const codeComplete = verificationValue.length === VERIFICATION_CODE_LENGTH;

    // DIS-1824: the code is sent only by VERIFY or Enter, never on the 6th
    // digit. Both call handleSubmit(onSubmit), so the guard lives in onSubmit:
    // it stops a second press sending the code again while the first send is
    // in flight.
    const submitOnceRef = useRef(false);

    const onSubmit = async (values: VerificationFormData) => {
      if (submitOnceRef.current) return;
      submitOnceRef.current = true;
      try {
        await _onSubmit(values, {
          setError,
        });
      } finally {
        submitOnceRef.current = false;
      }
    };

    const resendCode = async () => {
      reset(); // Clear the stale verify error before the new code arrives
      setResendError(undefined);
      setResending(true);

      // apiService can stall so set timeout here
      const resendTimeout = setTimeout(() => {
        setResending(false);
        setCodeResent(false);
      }, TIMEOUT);

      try {
        const result = await slowDown(_onResend());

        const success = result === true;
        setCodeResent(success);
        setResendError(success ? undefined : RESEND_ERROR_MESSAGE);
      } catch (_e) {
        setCodeResent(false);
        setResendError(RESEND_ERROR_MESSAGE);
      } finally {
        clearTimeout(resendTimeout);
        setResending(false);
        reset(); // Reset form after resending
      }
    };

    // Wrapper to handle async function for Text onPress (which expects sync handler)
    const handleResendPress = () => {
      resendCode().catch(error => {
        // Log error but don't crash - already handled in resendCode
        console.error('Resend code failed:', error);
      });
    };

    const isPhone = type === 'phone';

    // DIS-1824: an error shows only while the field holds 6 digits. At 6
    // digits the zod schema passes, so any error there is the one the caller
    // set after a send. Below 6 digits the only error is the zod length error,
    // which is not shown: VERIFY is disabled below 6 digits, so a part-typed
    // code is never marked invalid.
    const codeError = codeComplete ? errors.verification?.message : undefined;

    // DIS-1860: the email branch mirrors the SMS layout. The address goes on
    // its own centred line, as the formatted number does on the phone branch.
    const destination = isPhone
      ? formatMobileForDisplay(cleanPhoneNumber(phoneNumber ?? ''))
      : (emailAddress ?? '');

    const verificationView = (
      <PhoneVerificationView
        title={isPhone ? 'VERIFY YOUR MOBILE' : 'VERIFY YOUR EMAIL'}
        intro={`Enter the ${VERIFICATION_CODE_LENGTH}-digit code we sent to\n${destination}`}
        notice={
          codeResent
            ? `We have re-sent your code to your ${isPhone ? 'mobile' : 'email'}.`
            : undefined
        }
        otp={
          <Controller
            control={control}
            name="verification"
            render={({field: {onChange, onBlur, value}}) => (
              <Otp
                value={value}
                numInputs={6}
                onChangeText={onChange}
                onEnter={handleSubmit(onSubmit)}
                onBlur={onBlur}
                error={!!codeError}
                boxSize={isWide ? otpBoxSize : undefined}
              />
            )}
          />
        }
        error={codeError}
        verifyDisabled={!codeComplete || isSubmitting}
        verifying={isSubmitting}
        onVerify={handleSubmit(onSubmit)}
        resend={resending ? 'sending' : 'ready'}
        onResend={handleResendPress}
        resendError={resendError}
        // No "Change email" route exists, so the email branch shows no link.
        onChangeNumber={isPhone ? () => onBack() : undefined}
        onBack={onBack}
        centredOnLayout={isWide ? onLayout : undefined}
      />
    );

    // Wide: PhoneVerificationView draws the centred card and owns the page
    // padding, so there is no ContentView wrap (as RewardsVerificationScreen).
    return (
      <KeyboardAwareScrollView
        style={{backgroundColor: 'white', flex: 1}}
        contentContainerStyle={isWide ? {flexGrow: 1} : {flex: 1}}
        keyboardShouldPersistTaps="always"
        enableOnAndroid
        extraScrollHeight={40}>
        {isWide ? (
          verificationView
        ) : (
          <ContentView
            parentStyle={{flex: 1, paddingBottom: space.s16}}
            style={{flex: 1}}
            onLayout={onLayout}>
            {verificationView}
          </ContentView>
        )}
      </KeyboardAwareScrollView>
    );
  },
);

export default PhoneOrEmailVerification;
